import { HasAccess } from "@permify/react-role";
import { Button, Col, Modal, Popover, Row, Skeleton, Tag, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from 'react-router-dom';
import { deleteReservation } from '../../../services/reservation';
import History from '../../components/History';

const Header = ({ loading, setLoading, reservation, forecastData, patients }) => {

  const historyParams = {
    data: { type: 'reservations', id: reservation.id },
    types: ['reservations', 'reservations_payment_methods']
  }

  const [modalVisible, setModalVisible] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const showModal = () => setModalVisible(true);

  const closeModal = () => setModalVisible(false);

  const doDeleteReservation = async () => {
    try {
      setLoading((loading) => ({ ...loading, reservation: true }));
      const response = await deleteReservation({
        reservation_id: reservation.id,
      });
      if (response === 1) {
        message.success({
          content: `${t('reservations.header.reservation')} eliminada exitosamente`,
          style: {
            marginTop: '30vh',
            fontSize: 16,
          },
        });
        handleBack();
      } else {
        message.error({
          content: 'Ocurrió un error. Inténtalo de nuevo.',
          style: {
            marginTop: '30vh',
            fontSize: 16,
          },
        });
      }
    } catch (err) {
      console.error('Error interno:', err);
      message.error({
        content: `Error interno: ${err}`,
        style: {
          marginTop: '30vh',
          fontSize: 16,
        },
      });
    } finally {
      closeModal();
      setLoading((_loading) => ({ ..._loading, reservation: false }));
    }
  };

  const patientQty = patients?.length

  const aboveFiftyForecast = patients?.map((patient) =>
    patient.predict_percent >= 50
  )

  const willAttendPatients = aboveFiftyForecast?.filter((value) => value === true).length

  const patientsWithDocNumber = patients?.map((patient) =>
    patient.document_number !== null
  )
  const preAdmittedPatients = (patientsWithDocNumber?.filter((value) => value === true).length) ?? 0

  const handleBack = () => {
    navigate(-1)
  };

  const isLoading = loading.patients || patientQty === undefined || preAdmittedPatients === undefined || willAttendPatients === undefined

  return (
    <Row
      justify="center"
      className="reservation-settings-wrapper__header pt-20 pb-20"
    >
      <Col lg={22} xs={22}>
        <Row justify="space-between">
          <Col className="back-button-wrapper">
            <Button onClick={handleBack}>
              <i className="icon-system-chevron-left" />
              Volver
            </Button>
          </Col>
          {reservation.state === "drafted" &&
            reservation.deleted_at === null && (
              <Col className="delete-button-wrapper">
                <HasAccess
                  roles={['super_usuario', 'branch_office_manager', 'administracion']}
                  permissions='reservation-delete'
                >
                  <Button
                    onClick={showModal}
                    className="cmt-button secondary"
                    disabled={reservation.has_admissions}
                    type="primary"
                    size="small"
                  >
                    <svg
                      className="mr-10"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 148.61 189.36"
                    >
                      <g id="Capa_2" dataname="Capa 2">
                        <g id="Capa_1-2" dataname="Capa 1">
                          <path d="M47.16,21.25c0-3.94-.14-7.24,0-10.53C47.49,4.64,51.6.29,57.66.17q16.5-.33,33,0c6.37.11,10.48,4.42,10.78,10.79.15,3.28,0,6.58,0,10.4,11.53,0,22.48-.06,33.43,0,7.49,0,12.84,3.93,13.53,10a92.45,92.45,0,0,1,.07,10.16H.13c0-3.65-.34-7.14.08-10.54.69-5.58,5.89-9.6,12.18-9.65C23.71,21.19,35,21.25,47.16,21.25Zm46.89-.2c0-3.38,0-6.19,0-9C94,9,92.36,7.48,89.32,7.48c-10,0-20-.16-29.93.17-1.52,0-3.93,2.14-4.25,3.64-.67,3.1-.2,6.43-.2,9.76Z" />
                          <path d="M15.42,54.5v4.63q0,58.15,0,116.31c0,10.25,4.24,13.91,16.09,13.91q42.55,0,85.09,0c12.36,0,16.25-3.36,16.25-14.11q0-57.93,0-115.88V54.5Z" />
                        </g>
                      </g>
                    </svg>
                    {t('reservations.header.deleteReservationButton')}
                  </Button>
                </HasAccess>
              </Col>
            )}
        </Row>
        <Row justify="center">
          <Col lg={24} xs={24}>
            <h1 className="text-center mb-10">
              {t('reservations.header.title')} {reservation.number}
              {reservation.deleted_at !== null && (
                <Tag className="ml-10" color="red">
                  eliminada
                </Tag>
              )}
            </h1>
          </Col>
          <Col lg={15} className="reservation-state mb-20">
            <Row justify="center" align="middle">
              <Col style={{ display: "flex", alignItems: "center" }}>
                <p className="mb-lg-0 mr-lg-30 mr-md-5 mr-sm-5 mr-xs-5">
                  {t('reservations.header.status')}
                </p>
              </Col>
              <Col>
                <p className={`${['cancel', 'canceled-from-whatsapp', 'drafted'].includes(reservation.state) ? 'drafted' : 'confirmed'} mb-0`}>
                  {t(`reservations.header.reservationStatuses.${reservation.state}`, 'Estado no definido')}
                </p>
              </Col>
            </Row>
          </Col>
          <Col lg={24}>
            <Row justify="center" align="middle">
              {(!forecastData || isLoading) ? (
                <Col lg={15}>
                  <Skeleton active paragraph={{ rows: 1 }} />
                </Col>
              ) : (
                <>
                  <Col lg={15}>
                    <p className="text-title text-center mb-15">{t('reservations.header.forecastTitle')}&nbsp;&nbsp;
                      {willAttendPatients}/{preAdmittedPatients}
                      <Popover
                        trigger="hover"
                        overlayClassName="sidebar-tooltip"
                        placement="right"
                        content={
                          <p className="text-center mb-0">{t('reservations.header.forecastPopover')}</p>
                        }
                      ><span className="icon-alert-rounded ml-10" />
                      </Popover>
                    </p>
                  </Col>
                  <Col lg={15} className="reservation-forecast mb-10">
                    <Row justify="center" align="middle">
                      <Col>
                        <h4 className="text-center ml-10 mr-20">{t('reservations.header.reservedPatients')}:&nbsp;
                          {patientQty}</h4>
                      </Col>
                      <Col>
                        <h4 className="text-center ml-20"> {t('reservations.header.admittedPatients')}:&nbsp;
                          {preAdmittedPatients}</h4>
                      </Col>
                    </Row>
                  </Col>
                  <Col lg={22}>
                    <Row justify='end'>
                      <Col lg={4} xs={24}>
                        <Row gutter={[0, 15]}>
                          <Col lg={24} xs={24}>
                            <History
                              params={historyParams}
                            />
                          </Col>
                          <HasAccess
                            roles={['super_usuario', 'branch_office_manager', 'administracion']}
                            permissions='admission'
                          >
                            {
                              reservation.state === 'confirmed' &&
                              <Col lg={24} xs={24}>
                                <Link to={`/admisiones/${reservation.id}`}>
                                  <Button className="cmt-button secondary" type="primary">
                                    Admisión
                                  </Button>
                                </Link>
                              </Col>
                            }
                          </HasAccess>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </>
              )}
            </Row>
          </Col>
        </Row>
        <Modal
          className="deletion-modal"
          open={modalVisible}
          onCancel={closeModal}
          centered={true}
          closable={false}
          footer={false}
        >
          <Row justify="center" className="pb-10 pt-10">
            <Col lg={24} xs={24}>
              <p className="text-center mb-10">
                {t('reservations.header.modalTitle')} <strong>{reservation.number}</strong>
              </p>
            </Col>
            <Col lg={24} xs={24}>
              <p className="text-center mb-30">
                ¿Está seguro que desea eliminarla?
              </p>
            </Col>
            <Col lg={24} xs={24}>
              <Row justify="center">
                <Col
                  lg={7}
                  xs={22}
                  className="mr-lg-20 mb-lg-0 mb-md-15 mb-sm-15 mb-xs-15"
                >
                  <Button
                    className="cmt-button primary outline"
                    type="primary"
                    onClick={closeModal}
                    size="large"
                  >
                    No eliminar
                  </Button>
                </Col>
                <Col lg={10} xs={22}>
                  <Button
                    loading={loading.reservation}
                    onClick={doDeleteReservation}
                    classNarme="cmt-button primary"
                    type="primary"
                    size="large"
                    block
                  >
                    {t('reservations.header.modalTitle')}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Modal>
      </Col >
    </Row >
  );
};

export default Header;
